import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Globe, Sun, Moon, Shield, ArrowRight, Loader2, Phone, CreditCard, Mail, CheckCircle } from 'lucide-react';

export default function AuthPage() {
  const { login, verifyMfa, register, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const urlTab = new URLSearchParams(window.location.search).get('tab');
  const interpreterOnly = true; // Set to false when client registration is ready
  const [tab, setTab] = useState(urlTab === 'register' ? 'register' : 'login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaState, setMfaState] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  // Registration state
  const [regStep, setRegStep] = useState('form'); // 'form' | 'billing'
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ email: '', password: '', name: '', phone: '' });
  const [billingLoading, setBillingLoading] = useState(false);

  // Check if returning from Stripe billing setup
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const path = window.location.pathname;

    if (path === '/auth/billing-complete' && sessionId && user) {
      pollBillingStatus(sessionId);
    }
  }, [user]);

  const pollBillingStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 8) {
      setError('Billing status check timed out. Your payment may still be processing.');
      return;
    }
    try {
      const res = await authAPI.billingStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        window.location.href = '/client/onboarding';
        return;
      }
      setTimeout(() => pollBillingStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollBillingStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(loginForm.email, loginForm.password);
      if (result?.mfa_required) {
        setMfaState({ mfa_user_id: result.mfa_user_id });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaState.mfa_user_id, mfaCode);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid MFA code');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!regForm.phone.trim()) {
      setError('Phone number is required');
      return;
    }
    setLoading(true);
    try {
      await register({ ...regForm, role: 'client' });
      setRegStep('billing');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  const handleSetupBilling = async () => {
    setBillingLoading(true);
    setError('');
    try {
      const res = await authAPI.setupBilling({ origin_url: window.location.origin });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set up billing');
    }
    setBillingLoading(false);
  };

  const handleSkipBilling = () => {
    window.location.href = '/client/onboarding';
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Billing setup step (after registration)
  if (regStep === 'billing' && user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="billing-setup-page">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>Account Created!</h2>
            <p className="text-sm text-muted-foreground mt-2">Welcome, {user.name}. Complete your setup by adding a payment method.</p>
          </div>

          <div className="rounded-lg border bg-card p-6 space-y-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Add Payment Method</p>
                <p className="text-xs text-muted-foreground">$1.00 setup fee — includes 5 initial credits</p>
              </div>
            </div>
            <Button
              onClick={handleSetupBilling}
              disabled={billingLoading}
              className="w-full bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white"
              data-testid="setup-billing-button"
            >
              {billingLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Set Up Billing via Stripe
            </Button>
            <Button variant="ghost" className="w-full text-xs" onClick={handleSkipBilling} data-testid="skip-billing-button">
              Skip for now
            </Button>
          </div>

          <div className="flex items-center gap-3 rounded-lg border p-4 bg-secondary/50">
            <Mail className="w-5 h-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-sm font-medium">Verify your email</p>
              <p className="text-xs text-muted-foreground">We sent a verification link to <span className="font-medium">{user.email}</span></p>
            </div>
          </div>

          {error && <p className="text-sm text-destructive text-center" data-testid="billing-error">{error}</p>}
        </div>
      </div>
    );
  }

  // Billing complete (polling)
  const params = new URLSearchParams(window.location.search);
  if (window.location.pathname === '/auth/billing-complete' && params.get('session_id')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="billing-complete-page">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <h2 className="text-xl font-semibold">Processing your payment...</h2>
          <p className="text-sm text-muted-foreground">Please wait while we confirm your billing setup.</p>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left: Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden flex-col justify-between p-12">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <img src="/logo-white.png" alt="Interpreter IO" className="h-9" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground tracking-tight leading-tight mb-6" style={{ fontFamily: 'DM Sans' }}>
            {interpreterOnly ? <>Interpreter Portal<br />Sign In or Register</> : <>Human or AI-Powered<br />Language Interpreting</>}
          </h1>
          <p className="text-lg text-primary-foreground/70 max-w-md leading-relaxed">
            {interpreterOnly
              ? 'Access your interpreter dashboard. Manage your sessions, availability, and profile.'
              : 'The right interpreter, every time — AI or human interpreters. 250+ languages supported. AI when you want it. Human when you need it.'}
          </p>
        </div>
        <div className="relative z-10 flex gap-8 text-primary-foreground/60 text-sm">
          <div>
            <div className="text-3xl font-bold text-primary-foreground mb-1">250+</div>
            Languages
          </div>
          <div>
            <div className="text-3xl font-bold text-primary-foreground mb-1">&lt;45s</div>
            Connect Time
          </div>
          <div>
            <div className="text-3xl font-bold text-primary-foreground mb-1">24/7</div>
            Availability
          </div>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[hsl(var(--brand))] opacity-10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[hsl(var(--brand))] opacity-5 rounded-full translate-y-1/3 -translate-x-1/3" />
      </div>

      {/* Right: Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <img src="/logo-coloured.png" alt="Interpreter IO" className="h-7" />
            </div>
          </div>
          
          <div className="flex justify-end mb-6">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>

          {/* MFA Verification Step */}
          {mfaState ? (
            <div className="space-y-6" data-testid="mfa-verification">
              <div>
                <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>Two-Factor Authentication</h2>
                <p className="text-sm text-muted-foreground mt-1">Enter the 6-digit code from your authenticator app</p>
              </div>
              <form onSubmit={handleMfaVerify} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="mfa-code">Authentication Code</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                    data-testid="mfa-code-input"
                    autoFocus
                    required
                  />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
                <Button type="submit" className="w-full bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" disabled={loading || mfaCode.length !== 6} data-testid="mfa-submit-button">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Verify
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={() => { setMfaState(null); setMfaCode(''); setError(''); }}>
                  Back to Login
                </Button>
              </form>
            </div>
          ) : (
            <>
              <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8">
                  <TabsTrigger value="login" data-testid="login-tab">{interpreterOnly ? 'Interpreter Sign In' : 'Sign In'}</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">{interpreterOnly ? 'Interpreter Register' : 'Register'}</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email" type="email" placeholder="you@company.com"
                        value={loginForm.email}
                        onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))}
                        data-testid="login-email-input" required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password" type="password" placeholder="Enter your password"
                        value={loginForm.password}
                        onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                        data-testid="login-password-input" required
                      />
                    </div>
                    {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
                    <Button type="submit" className="w-full bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" disabled={loading} data-testid="login-submit-button">
                      {loading ? 'Signing in...' : 'Sign In'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>

                  {/* Google OAuth Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">or continue with</span></div>
                  </div>
                  <Button variant="outline" className="w-full gap-2" onClick={handleGoogleLogin} data-testid="google-login-button">
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Sign in with Google
                  </Button>

                  <div className="mt-6 p-4 rounded-lg bg-secondary">
                    <p className="text-xs text-muted-foreground mb-2">Demo accounts (after seeding):</p>
                    <div className="space-y-1 text-xs font-mono text-muted-foreground">
                      <p>Admin: admin@interpreterio.com / admin123</p>
                      <p>Client: client@acmelegal.com / client123</p>
                      <p>Interpreter: maria@interpreters.com / interp123</p>
                      <p>Org Admin: orgadmin@acmelegal.com / orgadmin123</p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="reg-name">Full Name</Label>
                      <Input id="reg-name" placeholder="John Doe" value={regForm.name} onChange={e => setRegForm(p => ({ ...p, name: e.target.value }))} data-testid="register-name-input" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email</Label>
                      <Input id="reg-email" type="email" placeholder="you@company.com" value={regForm.email} onChange={e => setRegForm(p => ({ ...p, email: e.target.value }))} data-testid="register-email-input" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input id="reg-phone" type="tel" placeholder="+1 (555) 123-4567" className="pl-10"
                          value={regForm.phone} onChange={e => setRegForm(p => ({ ...p, phone: e.target.value }))}
                          data-testid="register-phone-input" required />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-password">Password</Label>
                      <Input id="reg-password" type="password" placeholder="Create a password" value={regForm.password} onChange={e => setRegForm(p => ({ ...p, password: e.target.value }))} data-testid="register-password-input" required />
                    </div>

                    {/* Billing preview */}
                    <div className="rounded-lg border p-3 bg-secondary/50 flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-muted-foreground shrink-0" />
                      <div>
                        <p className="text-xs font-medium">Payment method required</p>
                        <p className="text-xs text-muted-foreground">You'll be redirected to Stripe after registration ($1.00 setup = 5 credits)</p>
                      </div>
                    </div>

                    {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
                    <Button type="submit" className="w-full bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" disabled={loading} data-testid="register-submit-button">
                      {loading ? 'Creating account...' : 'Create Account'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>

                  {/* Google OAuth for register too */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">or continue with</span></div>
                  </div>
                  <Button variant="outline" className="w-full gap-2" onClick={handleGoogleLogin} data-testid="google-register-button">
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Sign up with Google
                  </Button>
                </TabsContent>
              </Tabs>

              <div className="mt-8 flex items-center gap-2 text-xs text-muted-foreground">
                <Shield className="w-4 h-4" />
                <span>HIPAA-ready. Serving US, Canada, UK, and Australia.</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
